import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Radix Dialog: traps focus and closes on Esc for free (docs/06 §12). The price-changed modal in
 * the cart is the one that matters — a buyer must not be able to click past it into a submit.
 *
 * Open/close is ANIMATED with framer-motion — the engine every SPA already ships for the route
 * crossfade (see page-transition.tsx and the docs/06 §7 deviation note there); no second animation
 * library enters the bundle. Radix unmounts a closed dialog immediately, so the exit animation
 * needs `forceMount` + AnimatePresence, and AnimatePresence needs to know `open` — which the
 * composed `<DialogContent>` cannot see from props. The `Dialog` root therefore mirrors its open
 * state (controlled or uncontrolled) into a context that `DialogContent` reads. Call sites are
 * unchanged.
 *
 * Reduced motion follows the PageTransition rule: honored in JS, not merely CSS. framer-motion
 * writes inline transforms the global CSS clamp cannot reach, so under `prefers-reduced-motion`
 * we render the plain, statically-positioned Radix tree with no AnimatePresence at all.
 */

const DialogOpenContext = React.createContext(false);

export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: DialogPrimitive.DialogProps) {
  // Uncontrolled usage still works: the state Radix would have owned lives here instead, because
  // the context must reflect it either way.
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isOpen = open ?? uncontrolledOpen;

  return (
    <DialogOpenContext.Provider value={isOpen}>
      <DialogPrimitive.Root
        open={isOpen}
        onOpenChange={(next) => {
          setUncontrolledOpen(next);
          onOpenChange?.(next);
        }}
        {...props}
      >
        {children}
      </DialogPrimitive.Root>
    </DialogOpenContext.Provider>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

// The scrim is the theme's own ink at 50% (`foreground/50`), not a cold slate: it has to dim
// whatever surface is behind it, and that surface is warm stone in three of the four apps.
const overlayClasses = 'fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm';

// No translate utilities here — centering is part of the animated transform (x/y -50%), because
// framer-motion's inline `transform` would override a class-based translate and fling the panel
// into the top-left corner. The reduced-motion branch adds the static translate classes back.
const contentClasses =
  'fixed left-1/2 top-1/2 z-50 grid w-[calc(100%-2rem)] max-w-lg gap-4 rounded-lg border border-border bg-background p-6 shadow-lg max-h-[calc(100dvh-2rem)] overflow-y-auto';

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(overlayClasses, className)} {...props} />
));
DialogOverlay.displayName = 'DialogOverlay';

function DialogCloseButton({ label }: { label: string }) {
  return (
    <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <X className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </DialogPrimitive.Close>
  );
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeLabel?: string }
>(({ className, children, closeLabel = 'Close', ...props }, ref) => {
  const open = React.useContext(DialogOpenContext);
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(contentClasses, '-translate-x-1/2 -translate-y-1/2', className)}
          {...props}
        >
          {children}
          <DialogCloseButton label={closeLabel} />
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }

  return (
    <AnimatePresence>
      {open ? (
        <DialogPortal forceMount>
          <DialogPrimitive.Overlay asChild forceMount>
            <motion.div
              className={overlayClasses}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.12, ease: 'easeIn' } }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            />
          </DialogPrimitive.Overlay>
          <DialogPrimitive.Content ref={ref} asChild forceMount {...props}>
            {/* y stays in percent (-46% → -50%) rather than a calc() offset: same-unit values
                interpolate cleanly, and the rise scales with the panel like the centering does. */}
            <motion.div
              className={cn(contentClasses, className)}
              initial={{ opacity: 0, x: '-50%', y: '-46%', scale: 0.96 }}
              animate={{ opacity: 1, x: '-50%', y: '-50%', scale: 1 }}
              exit={{
                opacity: 0,
                x: '-50%',
                y: '-48%',
                scale: 0.98,
                transition: { duration: 0.12, ease: 'easeIn' },
              }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              {children}
              <DialogCloseButton label={closeLabel} />
            </motion.div>
          </DialogPrimitive.Content>
        </DialogPortal>
      ) : null}
    </AnimatePresence>
  );
});
DialogContent.displayName = 'DialogContent';

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
